import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import ListItemText from '@mui/material/ListItemText'
import React from 'react'
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from 'react-beautiful-dnd'
import { PublicTransportStationFeature } from '@digimap/types'
import CityMapperLink from '../../../components/CityMapper/CityMapperLink'

interface ReorderablePublicTransportListProps {
  stations?: PublicTransportStationFeature[]
  visible: boolean
  onReorder: (items: PublicTransportStationFeature[]) => void
  onRemove: (item: PublicTransportStationFeature) => void
  isStart: boolean
}

const reorder = (
  list: PublicTransportStationFeature[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined
) => ({
  background: isDragging ? 'lightgreen' : 'inherit',
  ...draggableStyle,
})

export default function ReorderablePublicTransportList({
  stations,
  visible,
  onReorder,
  onRemove,
  isStart,
}: ReorderablePublicTransportListProps) {
  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }
    if (!stations) return

    const newItems = reorder(
      stations,
      result.source.index,
      result.destination.index
    )

    onReorder(newItems)
  }

  if (!visible) {
    return null
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='droppable'>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {stations &&
              stations.map((station, index) => (
                <Draggable
                  key={`${station.properties.name || 'untitled'}-${index}`}
                  draggableId={`${
                    station.properties.name || 'untitled'
                  }-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <ListItem
                      dense
                      ContainerComponent='div'
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                      )}
                    >
                      <ListItemIcon>
                        <Icon>drag_handle</Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <CityMapperLink
                            feature={station}
                            type={isStart ? 'outbound' : 'inbound'}
                          />
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => {
                            onRemove(station)
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  )}
                </Draggable>
              ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
